/* Note photo storage and hydration
 *
 * Moved verbatim from currencykeep_2026-08-03.html, lines 614-636.
 * The body below is byte-identical to the original; only the import
 * block above and the export block below were added. (Phase 1, Step 2.)
 */

import { isCol, isObs } from './catalog.js'
import { chipBody, isErr, republish } from './community.js'
import { loadCommunityThumbs } from './community-photos.js'
import { chip } from './render.js'
import { $, save, state, toast } from './state.js'
import { getToken } from './auth.js'
import { STATE, TIER_FOR, resolveSlot, slotState } from './photo-view.js'
import { idbRef, parsePhotoRef } from '../shared/photo-ref.js'
import { HASH_KEY, PHOTO_KEY, RAW_KEY, SPEC_KEY } from './photo-keys.js'
import { HAS, storageDel, storageSet } from './storage.js'
import { contentHashOf, decodeDataUrl, onUploadStatus, uploadStatus } from './migrate.js'

/* ---------- photos ---------- */

/* ── ⚠⚠ A NOTE PHOTO IS THREE THINGS AS OF PHASE 4 STEP 6 ───────────
 *   photo:<id>:<slot>      the PREVIEW — a data URL, for display.
 *   photoraw:<id>:<slot>   the ORIGINAL FILE, exactly as the camera or
 *                          the scanner wrote it. A Blob, not a string.
 *   photospec:<id>:<slot>  the transform `{angle, crop}` as JSON.
 *
 * ⚠ THE PREVIEW KEY AND ITS VALUE ARE UNCHANGED FROM PHASE 1, AND THAT
 * IS THE POINT. Every reader — `getPhoto`, `hydratePhotos` — keeps
 * working untouched, and a photo added before Step 6 simply has no
 * sidecar. Nothing migrates. (⚠ `publishAllPhotos` and `getPubPhoto`
 * were named here too until Step 7 bullet 3 deleted them, 2026-08-28.)
 * Jesse, 2026-08-21: *"existing idb: photos stay where they are, and the
 * migration stays in Step 7 where it belongs."*
 *
 * ⚠ THE ORIGINAL IS NOT PUBLISHED, AND AS OF 2026-08-28 NOTHING IS.
 * There is no public COPY of a photograph any more — Step 7 bullet 3
 * deleted the `pubph:` write. A stranger is served the `thumb` tier out
 * of R2 by the batch route; the original never leaves the bucket, which
 * is the same promise as before, kept by a different mechanism.
 */
/* ⚠ THE THREE BUILDERS MOVED TO `photo-keys.js` — Phase 4 Step 7, Act 2.
 * They are still exported from here, so every importer is untouched.
 * They moved because this module pulls in the catalog, which FETCHES at
 * load, and the migration needs the key grammar without the app. */

const photoCache={};
async function getPhoto(id){
  if(photoCache[id]!==undefined)return photoCache[id];
  if(!HAS){photoCache[id]=null;return null;}
  const g=async s=>{try{const r=await window.storage.get(PHOTO_KEY(id,s),false);return r&&r.value?r.value:null;}catch(e){return null;}};
  const parts=await Promise.all([g("front"),g("back")]);
  const o={};if(parts[0])o.front=parts[0];if(parts[1])o.back=parts[1];
  photoCache[id]=(o.front||o.back)?o:null;
  return photoCache[id];
}

/* ── ⚠⚠ FORGET ONE SLOT'S LOCAL PREVIEW. ACT 4, PHASE 4 STEP 7 ───────
 * `photoCache` is read BEFORE storage by `getPhoto`, so a preview
 * deleted from IndexedDB keeps rendering from memory until the next
 * reload. For Act 4 that is not cosmetic: the whole point of retiring a
 * preview is that the slot now resolves through the bucket, and a stale
 * cache entry means the bucket path silently does not run. Act 3 would
 * look proven, and a fault in it would surface a page load later on a
 * different day with nothing pointing back at the deletion.
 *
 * That is bug 1's shape — *"a silent failure delayed by one page load"* —
 * arriving from the other direction, which is why the delete path evicts
 * rather than leaving it to a refresh.
 *
 * ⚠ IT TOUCHES THE CACHE AND NOTHING ELSE. No storage write, no ref
 * change, no `save()`. A slot whose ref is `r2:` still classifies as
 * REMOTE with or without a local copy (`photo-view.js:slotState`), so
 * there is nothing about the row for this to update. */
export function forgetPhotoSlot(id,slot){
  const p=photoCache[id];
  if(!p)return false;
  if(p[slot]===undefined)return false;
  delete p[slot];
  photoCache[id]=(p.front||p.back)?p:null;
  return true;
}

/**
 * Store one note photo. Returns `{ keptOriginal }`.
 *
 * ── ⚠ THE ORIGINAL IS WRITTEN FIRST, AND THAT ORDER IS DELIBERATE ────
 * Storage can be full. Writing the preview first and the original second
 * means the case that fails is the one Step 6 exists to protect, every
 * time. Writing the original first gives it the first chance at the
 * quota, and the collector is TOLD when it did not get one.
 *
 * ── ⚠⚠ A FAILED REPLACE DELETES THE OLD SIDECAR, AND IT MUST ─────────
 * If the caller is replacing a photo and the new original cannot be
 * stored, leaving the OLD original in place would leave the sidecar
 * describing one picture and the preview describing another — and Step 7
 * would upload the old photograph under the new photograph's identity.
 * A silent wrong picture is exactly trap 2, so a half-write is cleaned
 * out rather than left to be discovered later.
 */
async function putPhoto(id,slot,{preview,original=null,spec=null}){
  const before=photoCache[id]||(await getPhoto(id))||{};
  const p=Object.assign({},before);p[slot]=preview;
  let keptOriginal=false;
  if(HAS&&original&&spec){
    try{
      await storageSet(RAW_KEY(id,slot),original,false);
      await storageSet(SPEC_KEY(id,slot),JSON.stringify(spec),false);
      keptOriginal=true;
    }catch(e){
      console.error("original not kept",e);
      try{await storageDel(RAW_KEY(id,slot),false);}catch(e2){}
      try{await storageDel(SPEC_KEY(id,slot),false);}catch(e2){}
    }
  }
  /* ══ ⚠⚠⚠ A FAILED SAVE IS A FAILED SAVE — 2026-08-25, JESSE ═══════════
   *
   * THIS BLOCK USED TO CATCH THE ERROR, TOAST *"Photo too large to save"*,
   * AND THEN CARRY ON TO SET `hasFront = true` AND WRITE AN `idb:` REF
   * ANYWAY — and `editor.js` then said **"Photo saved"** over the top.
   *
   * ⚠⚠ SO A QUOTA FAILURE PRODUCED A ROW CLAIMING A PHOTOGRAPH THAT WAS
   * NEVER STORED, and every later reader believed it: `hasFront` renders a
   * slot, `holdingToRow` writes `idb:<id>:<slot>`, `photo-census` counts it
   * as a photograph to migrate, and Act 2 would find nothing to upload.
   * **A ref is a claim that bytes exist somewhere. Writing one when the
   * write failed makes the claim false at the moment it is made.**
   *
   * ⚠ JESSE'S FRAMING, AND IT IS WHY THIS IS FIXED BEFORE ANYTHING ELSE:
   * *"A note claiming a photograph that was never stored is the exact
   * failure this whole phase exists to remove."*
   *
   * ── WHAT IT DOES NOW ────────────────────────────────────────────────
   * The preview write decides everything. If it fails, NOTHING downstream
   * runs: no `hasFront`, no ref, no `save()`, no public copy — and the
   * sidecar written above is cleaned out, because an original describing a
   * photograph with no preview is the torn state this function already
   * refuses in the other direction.
   *
   * ⚠ THE CACHE IS ROLLED BACK TOO, and that is not tidiness. Leaving the
   * new preview in `photoCache` would show the collector their photograph
   * until the next reload and then lose it — which is the silent failure,
   * just delayed by one page load.
   *
   * ⚠ `!HAS` IS A SUCCESS, NOT A FAILURE. With no storage backend the app
   * has always run in memory and rendered the photo; that is the
   * catalog-browsing path and it is unchanged. */
  let previewSaved=!HAS;
  if(HAS){
    try{await storageSet(PHOTO_KEY(id,slot),preview,false);previewSaved=true;}
    catch(e){console.error("photo save failed",e);}
  }
  if(!previewSaved){
    if(keptOriginal){
      try{await storageDel(RAW_KEY(id,slot),false);}catch(e2){}
      try{await storageDel(SPEC_KEY(id,slot),false);}catch(e2){}
      keptOriginal=false;
    }
    /* ⚠ THE OLD HASH GOES TOO. The sidecar cleanup above may have taken
     * the previous original with it, leaving the previous PREVIEW as what
     * an upload would send — and a hash of a file that is no longer there
     * would refuse that upload for ever. An absent hash bites nothing. */
    try{await storageDel(HASH_KEY(id,slot),false);}catch(e2){}
    photoCache[id]=(before.front||before.back)?before:null;
    return {keptOriginal:false,previewSaved:false,saved:null};
  }
  photoCache[id]=p;
  /* ══ THE FOURTH KEY — PHASE 6.5 STEP 1, 2026-09-13 ═══════════════════
   * The SHA-256 of the bytes an upload of this slot will SEND: the
   * original when it was kept, else the preview — the same choice
   * `readLocalPhoto` makes, and the same digest the endpoint computes over
   * the body. Written AFTER the preview, so a slot never carries a hash
   * for a photograph that was not stored. See `photo-keys.js` for why a
   * photograph needs to know its own hash, and why an absent one means
   * nothing. ⚠ Cost on a phone for a 24 MP HEIC: unmeasured; read off the
   * console on the walk. */
  if(HAS){
    try{
      let bytes=null;
      if(keptOriginal){bytes=new Uint8Array(await original.arrayBuffer());}
      else{const d=decodeDataUrl(preview);if(d.ok)bytes=d.bytes;}
      const hash=bytes?await contentHashOf(bytes):null;
      if(hash)await storageSet(HASH_KEY(id,slot),hash,false);
      else await storageDel(HASH_KEY(id,slot),false);
    }catch(e){
      console.error("photo hash not kept",e);
      try{await storageDel(HASH_KEY(id,slot),false);}catch(e2){}
    }
  }
  /* ── ⚠⚠ A LOCAL WRITE CLEARS THE CARRIED REF FOR THIS SLOT ──────────
   * PHASE 4 STEP 7, ACT 2's PREREQUISITE. `mapper.js:refOut` preserves an
   * `r2:` ref through the round trip so a migration is not undone by the
   * next save. That is right for a photograph nobody has touched since,
   * and WRONG the moment one is replaced here: the bytes that just landed
   * in IndexedDB are not the bytes in the bucket, so keeping
   * `r2:<oldhash>` would leave the row pointing at the PREVIOUS
   * photograph while the collector looks at the new one.
   *
   * ⚠ NULLING IT IS WHAT MAKES THE SLOT `idb:` AGAIN, which is exactly
   * true — the new photograph IS only in the browser — and it puts the
   * slot back in the migration's queue rather than out of it. */
  /* ══ ⚠⚠⚠ ONLY THE SLOT THAT WAS WRITTEN — 2026-08-25 ═══════════════
   * This line used to derive BOTH booleans from the LOCAL cache:
   *     h.hasFront = !!p.front ;  h.hasBack = !!p.back
   * and `p` comes from `getPhoto`, which reads IndexedDB. So on a note
   * whose OTHER slot claimed a photograph with no local bytes, `p.back`
   * was undefined, `hasBack` went FALSE, and `refOut` wrote NULL over
   * that slot's ref. **Adding a front photograph deleted the back ref** —
   * not the bytes, which were already gone, but the last record that a
   * photograph was ever meant to be there. Proven in `smoke`.
   *
   * ⚠ THE OTHER SLOT IS NOT THIS CALL'S BUSINESS. The ROW is the truth
   * about it, and the row is not something the local cache gets a vote on.
   *
   * ⚠⚠ AND THE REF IS SET TO WHAT WILL ACTUALLY BE PERSISTED, NOT NULL.
   * Nulling it left memory and Postgres disagreeing — `refOut` mints
   * `idb:<id>:<slot>` on the way out, so the database ended up with a ref
   * the client did not think it had. `planMigration` reads memory, so its
   * answer depended on whether the page had been reloaded, which is not a
   * property a migration planner may have. */
  const h=state.collection.find(x=>x.id===id);
  /* ⚠ THE ROW WRITE IS RETURNED, NOT ONLY FIRED — 2026-09-13. The instant
   * upload waits on it before sending, so a landing's `r2:` PATCH cannot
   * be overwritten by this earlier-serialised `idb:` full replace. */
  let saved=null;
  if(h){
    if(slot==='front'){h.hasFront=true;h.frontRef=idbRef(id,'front');}
    else if(slot==='back'){h.hasBack=true;h.backRef=idbRef(id,'back');}
    saved=save();
  }
  /* ⚠⚠ THE `pubph:` WRITE WAS DELETED HERE — Phase 4 Step 7 bullet 3,
   * 2026-08-28. It copied the preview into
   * `notevault:pubph:<user>:<id>:<slot>`, a key `remote.js` routes to
   * LOCAL IndexedDB, so it duplicated every photograph into a store no
   * other browser could read. The bytes a stranger sees come from R2.
   *
   * ⚠⚠⚠ `republish()` STAYS, AND IT IS NOT LEFTOVER. It rewrites the
   * public snapshot so `hasF`/`hasB` become true for this holding —
   * which is what gives the tile an id in `views-community.js:46` and
   * therefore what puts it in the batch request at all. Delete this call
   * and a newly photographed note silently stops appearing to strangers. */
  if(state.isPublic&&state.username)republish();
  return {keptOriginal,previewSaved:true,saved};
}

/* ══ THE BADGE WHILE AN UPLOAD IS IN FLIGHT — PHASE 6.5 STEP 1 ═════════
 * While `uploadSlot` holds a slot, the frame's `.pend` badge reads the
 * phase instead of *On this device only*, and a 3 px strip along the
 * frame's bottom edge fills with bytes sent. Two phases, and the second
 * has no number — see `photo-xhr.js` for why. The vault chip's sage dot
 * is unchanged: it answers "is this in the bucket", which is still no.
 *
 * ⚠ TICKS WRITE INTO THE EXISTING ELEMENTS BY ID. A full `render()` per
 * progress event would re-hydrate every photograph on the screen; a
 * render that happens anyway mid-flight rebuilds the badge from
 * `uploadStatus` and loses nothing. When a slot settles (`s` null) the
 * landing's own `onChange` renders, so this listener does nothing. */
function uploadPhase(s){
  if(!s)return null;
  const pct=s.total>0?Math.min(100,Math.round(s.sent/s.total*100)):0;
  if(s.phase==="processing")return {text:"Processing…",pct:100};
  if(s.phase==="sending")return {text:"Sending "+pct+"%",pct};
  return {text:"Waiting to upload",pct:0};
}
function uploadBadge(id,slot,pending){
  const s=uploadStatus(id,slot);
  if(s){const ph=uploadPhase(s);return '<span class="pend busy"><span class="up">↑</span><span class="uptext">'+ph.text+'</span></span><span class="upbar" style="width:'+ph.pct+'%"></span>';}
  return pending?'<span class="pend"><span class="up">↑</span>On this device only</span>':'';
}
onUploadStatus((id,slot,s)=>{
  if(!s||state.screen!=="holding"||state.sel!==id)return;
  const f=document.getElementById("pf_"+slot);if(!f)return;
  const t=f.querySelector(".uptext"),b=f.querySelector(".upbar");
  if(!t||!b)return;
  const ph=uploadPhase(s);t.textContent=ph.text;b.style.width=ph.pct+"%";
});
function holdChip(h,n){return `<div class="chip ${isErr(n)?"err":""}${isCol(n)?" col":""}${isObs(n)?" obs":""}" id="th_${h.id}">${chipBody(n)}</div>`;}
/* ── ⚠⚠⚠ A SLOT THAT CLAIMS A PHOTOGRAPH NOBODY HAS ─────────────────
 * 2026-08-25, JESSE. Four of his notes carry `idb:` refs whose bytes are
 * not in this browser and, as far as anything can be told, were never
 * written — the quota bug above wrote the ref anyway and the cache hid it
 * until the next reload.
 *
 * ⚠⚠ UNTIL NOW THE APP RENDERED THAT AS "Add front" — BYTE-FOR-BYTE THE
 * SAME THING IT SHOWS FOR A NOTE NOBODY EVER PHOTOGRAPHED. That is the
 * `null` vs `malformed` collapse `shared/photo-ref.js` refuses one level
 * up, committed one level down: "this slot is empty" and "this slot says
 * it has a photograph and it is not here" became one answer, silently.
 *
 * ⚠ IT DOES NOT SAY WHICH CASE IT IS, AND THAT IS DELIBERATE. "Not on
 * this device" is true whether the bytes are on another origin or gone.
 * "Lost" would be a guess, and this project has just measured what
 * guessing costs.
 *
 * ⚠ IT REPAIRS NOTHING. The ref is not cleared, `hasFront` is not
 * flipped. That ref is the only surviving record that a photograph was
 * meant to exist here, and clearing it is a database write against the
 * collector's own rows — their decision, not a side effect of rendering.
 *
 * ⚠ AN `r2:` REF IS NOT THIS STATE. Its bytes are in the bucket and
 * fetching them is Act 3's read swap. Marking it "missing" would be
 * wrong the day that lands, so it is excluded by scheme rather than by
 * being forgotten. */
function claimsMissingPhoto(h, slot, local) {
  if (local && local[slot]) return false
  if (!(slot === 'front' ? h.hasFront : h.hasBack)) return false
  return parsePhotoRef(slot === 'front' ? h.frontRef : h.backRef).scheme !== 'r2'
}

/* ⚠ ONE CALL SITE FOR THE SEAM, so every surface asks the same way and a
 * future one cannot quietly invent its own rule. The token is passed as a
 * FUNCTION rather than a value: `getToken` refreshes a near-expired
 * session, and reading it once at render time would hand a stale token to
 * every photograph on a screen that stays open. */
const view = (h, slot, tier, localUrl) =>
  resolveSlot({ holding: h, slot, tier, localUrl, tokenImpl: getToken })

/* ══ ⚠⚠⚠ "IS IT IN THE BUCKET" IS A QUESTION ABOUT THE REF ═══════════
 *
 * **THE BUG THIS EXISTS TO STOP, FOUND ON THE SWEEP'S FIRST REAL RUN,
 * 2026-08-28.** The pending badge was first keyed off
 * `resolveSlot().state === LOCAL`, and it stayed on a photograph the
 * sweep had already uploaded — through a reload, with the row reading
 * `r2:`.
 *
 * ⚠⚠ BOTH ANSWERS WERE CORRECT; THE QUESTION WAS WRONG.
 * `slotState` answers **what the ref says** — `REMOTE` for `r2:`.
 * `resolveSlot` answers **where these pixels came from**, and
 * `photo-view.js:214` is local-first: with a local copy present it
 * serves that copy and reports `LOCAL` whatever the ref says. That is
 * Jesse's own quality bar — *"top quality at all times, ready
 * instantly"* — and it is not a defect.
 *
 * **The badge asks "is this photograph in the bucket", which is a
 * question about the REF, and it must be answered by the ref.** Reading
 * it off the renderer meant the badge said "On this device only" about a
 * photograph that was in the bucket AND on this device — true of the
 * pixels, false of the claim.
 *
 * ⚠ THE SAME SHAPE AS THE TWO ROUTE SWALLOWS FIXED THIS SESSION AND AS
 * THE TOAST DEFECT FOUND BESIDE IT: a signal that answers a different
 * question than the one being asked, believed because it was nearby. */
const pendingUpload = (h, slot, hasLocal) => slotState(h, slot, hasLocal) === STATE.LOCAL

async function hydratePhotos(){
  for(const h of state.collection){
    const el=document.getElementById("th_"+h.id);if(!el)continue;
    const p=await getPhoto(h.id);
    const r=await view(h,"front",TIER_FOR.chip,(p&&p.front)||null);
    if(r.url){el.classList.add("hasimg");el.innerHTML='<img alt="note">';el.firstChild.src=r.url;}
    else if(r.state!==STATE.EMPTY)el.classList.add("noimg");
    /* ⚠ THE DOT IS ON THE CHIP TOO, NOT ONLY THE NOTE DETAIL. His reason
     * for wanting a state rather than a toast was that the vault should
     * be able to tell him without his having caught a message — and the
     * vault is this grid.
     * ⚠⚠ FROM THE REF, NOT FROM `r.state` — see `pendingUpload`. */
    el.classList.toggle("pending",pendingUpload(h,"front",!!(p&&p.front)))
  }
  if(state.screen==="holding"){const h=state.collection.find(x=>x.id===state.sel);if(h){const p=(await getPhoto(h.id))||{};
    /* ⚠ WHAT THIS PASS ACTUALLY PUT ON EACH FRAME, keyed by slot. The
     * vault enlarge below spends an `archive` fetch only for a slot this
     * pass filled — the same `shown[z]` condition the Community enlarge
     * uses, and for the same reason: a viewer left open over a side that
     * has since gone empty, missing or unavailable must ask for nothing. */
    const shown={};
    /* ══ ⚠⚠ THE TWO SLOTS RESOLVE IN PARALLEL — JESSE, 2026-09-03, AND
     * IT IS A MITIGATION RATHER THAN A FIX ═══════════════════════════
     *
     * These were awaited one after the other, so the BACK frame waited
     * for the front's fetch as well as its own. Measured 2026-09-03: at
     * 600 ms latency the front frame stopped being a replace target
     * after 613 ms and the back after 1,215 ms; at 2 s, 2.0 s and 4.0 s.
     *
     * ⚠⚠⚠ THIS DOES NOT CLOSE THE HYDRATION WINDOW AND MUST NOT BE READ
     * AS CLOSING IT. A filled frame carries `data-photoadd` until its
     * own display fetch returns, because that is when `r.url` arrives.
     * What this removes is the DOUBLING — the back frame goes from 2x
     * latency to 1x. **The window stands, open and recorded**, and Jesse
     * ruled deliberately against the four options that would close it.
     *
     * ⚠ THE `!f` CHECK STAYS AHEAD OF THE REQUEST. It used to guard the
     * fetch by sitting before it in the loop; filtering the slot list
     * first keeps that exactly — a frame with no element still costs
     * nothing on the wire.
     *
     * ⚠ THE DOM WRITES STAY ORDERED, front then back. Only the waiting
     * is concurrent; nothing below this line races. */
    const slots=["front","back"].filter(s=>document.getElementById("pf_"+s));
    const resolved=await Promise.all(slots.map(s=>view(h,s,TIER_FOR.detail,p[s]||null)));
    for(let i=0;i<slots.length;i++){
      const slot=slots[i];
      const f=document.getElementById("pf_"+slot);if(!f)continue;
      const r=resolved[i];
      if(r.url)shown[slot]=r.url;
      /* ══ ⚠⚠⚠ `LOCAL` IS "PENDING UPLOAD", AND IT NOW SAYS SO ═════════
       * PHASE 4 STEP 7 BULLET 4, 2026-08-28. `classify` returns `LOCAL`
       * for an `idb:` ref whose bytes are present, and since Act 3 that
       * means exactly one thing: **the photograph is in this browser and
       * NOT in the bucket.** No new state was invented for it — `LOCAL`
       * already meant this and merely looked like `filled`.
       *
       * ⚠⚠ IT RENDERS THE PHOTOGRAPH NORMALLY AND ADDS A BADGE. Jesse:
       * *"this one means 'still uploading, nothing is wrong yet'"* — so
       * the frame keeps `filled`, keeps `Replace`, and stays tappable.
       * Degrading it would be a lie in the other direction: the picture
       * is right there and instant, which is the whole local-first bar.
       *
       * ⚠ AND IT MUST NOT LOOK LIKE `MISSING`, his explicit condition —
       * different colour (sage, not amber), different glyph (↑, not ⚠),
       * and a filled frame rather than an empty one. */
      if(r.url){
        /* ⚠⚠ FROM THE REF, NOT FROM `r.state` — see `pendingUpload`. A
         * photograph already in the bucket is still SERVED from the local
         * copy while one exists, so `r.state` is LOCAL for slots that are
         * not pending anything. */
        const pending=pendingUpload(h,slot,!!p[slot])
        /* ══ ⚠⚠⚠ A FILLED FRAME STOPS BEING A REPLACE TARGET — THE
         * 2026-08-04 REGISTER ITEM, DECIDED BY JESSE 2026-09-03 ════════
         *
         * ⚠ THE `removeAttribute` IS THE SAFETY PROPERTY AND THE REST IS
         * THE FEATURE. Until now this branch left `data-photoadd` on the
         * frame and made `Replace` a mere label, so the whole 192×120
         * tile opened the file picker — one step from overwriting an
         * irreplaceable photograph. The attribute moves to the chip, and
         * the tile answers a tap with the viewer instead. **What matters
         * is where a MISS lands**, so adding the viewer without taking
         * the attribute off would have built the feature and left the
         * defect.
         *
         * ⚠⚠ THE CHIP IS A `<button>` CARRYING `data-photoadd`, NESTED
         * INSIDE THE FRAME THAT CARRIES `data-vzoom`. Both handlers see
         * a chip click — `closest()` walks up through the frame — so the
         * viewer handler in `main.js` bails when the click also hit a
         * `[data-photoadd]`. That guard is not optional and it is not
         * defensive: without it one tap on Replace opens the picker AND
         * the viewer.
         *
         * ⚠ IT IS `.edit.repl`, NOT `.edit`. The MISSING branch below
         * uses a plain `.edit` chip reading "Add" which is still only a
         * LABEL on a tile that is itself the target; the 44px sizing
         * belongs to the one that is a real button. Same class for both
         * would have grown a label to a tap target's size. */
        f.classList.add("filled");f.classList.toggle("pending",pending||!!uploadStatus(h.id,slot))
        f.removeAttribute("data-photoadd")
        f.setAttribute("data-vzoom",slot)
        f.innerHTML='<img alt="'+slot+'"><span class="tag">'+slot+'</span>'
          +uploadBadge(h.id,slot,pending)
          +'<button class="edit repl" data-photoadd="'+h.id+':'+slot+'">Replace</button>'
        /* ══ ⚠⚠⚠ A TAKE PHOTO CHIP SAT HERE FOR ONE DAY AND JESSE
         * REMOVED IT — 2026-09-06, AFTER WALKING IT ON BOTH DEVICES ══
         *
         * ⚠ IT WORKED. It appeared correctly on filled tiles and empty
         * frames on Mac and phone; **this is a REDUNDANCY ruling, not a
         * defect.** His reason, and it is narrower than the one he first
         * gave: *"on the filled tile it competes with Replace for the
         * same corner region on a tile I have already ruled twice
         * about."*
         *
         * ⚠⚠⚠ AND THE COST IS HIS, TAKEN KNOWINGLY, RECORDED SO NOBODY
         * "COMPLETES" IT LATER: **ON A FILLED TILE THERE IS NOW NO
         * CAMERA ROUTE AT ALL.** Re-shooting a photograph is Replace →
         * the platform sheet → Take Photo — **three taps, where the
         * EMPTY frame is one.** That asymmetry is deliberate. The empty
         * frame keeps its chip precisely because one tap versus
         * tap-then-choose is not redundancy; the filled tile loses it
         * because a second 44px chip on a 192 × 120 tile is. */
        f.querySelector("img").src=r.url;continue;
      }
      /* ⚠⚠ UNAVAILABLE IS NOT MISSING, AND THE FRAME STOPS BEING A BUTTON.
       * A slot whose bytes are in the bucket but did not load is a
       * TRANSIENT failure. Leaving `data-photoadd` on it would mean one
       * tap opens the editor over a photograph that is perfectly fine and
       * merely offline — replacing a good picture because the network was
       * slow. The attribute is removed so the tap does nothing. */
      if(r.state===STATE.UNAVAILABLE){f.classList.add("unavailable");f.removeAttribute("data-photoadd");f.innerHTML='<div class="ph"><span class="cam">⟳</span>Couldn\'t load — check your connection</div><span class="tag">'+slot+'</span>';continue;}
      /* ⚠ MISSING KEEPS THE WHOLE TILE AS THE PICKER. An `idb:` ref whose
       * bytes are not in this browser: nothing exists to open, so the
       * tile has no enlarge to offer and adding a photograph is the only
       * thing a collector can do here. Unchanged, and Jesse confirmed it
       * against the code before the split. */
      if(r.state===STATE.MISSING){f.classList.add("missing");f.innerHTML='<div class="ph"><span class="cam">⚠</span>Photograph not on this device</div><span class="tag">'+slot+'</span><span class="edit">Add</span>';continue;}
      /* ══ ⚠⚠⚠ BROKEN IS INERT, AND IT IS SPLIT FROM MISSING BECAUSE THE
       * SHARED SENTENCE WAS FALSE — JESSE, 2026-09-03 ═════════════════
       *
       * ⚠ BROKEN HAS EXACTLY ONE CAUSE: `parsePhotoRef` returned
       * `malformed`, so **the pointer is unreadable.** It is NOT a
       * render failure and NOT a decode failure — there is no such path
       * in `slotState`. The row claims a photograph and the bytes may be
       * sitting intact in the bucket; what is lost is the ability to say
       * where. So *"Photograph not on this device"* asserted a location
       * nobody knows, and it was false here.
       *
       * ⚠⚠ THE TILE IS INERT — `data-photoadd` COMES OFF, as it does for
       * UNAVAILABLE, AND THAT IS THE RULING. ⚠ It carries its OWN class,
       * `.broken`, grouped with `.unavailable` in the stylesheet so the
       * two look identical while staying distinguishable in the DOM:
       * unavailable is transient and broken is permanent, and collapsing
       * two photo states into one marker is a mistake this project has
       * already paid for twice. Offering Replace over a
       * photograph that may be perfectly recoverable is the overwrite
       * this whole item exists to prevent, and it is worse here than on
       * a filled tile: **the collector cannot see what they would be
       * destroying.**
       *
       * ⚠⚠⚠ THE COST IS THAT THERE IS NO WAY FORWARD FROM THIS TILE, AND
       * JESSE TOOK IT DELIBERATELY. A retry was considered and rejected
       * as a button that cannot work — re-reading the same stored string
       * fails identically. A real repair would have to re-fetch the
       * HOLDING ROW, not the photograph, and no such path exists.
       * ⚠ The copy says the least that is true, on purpose: naming a
       * damaged record would promise a repair the app does not have. */
      if(r.state===STATE.BROKEN){f.classList.add("broken");f.removeAttribute("data-photoadd");f.innerHTML='<div class="ph"><span class="cam">⚠</span>This photograph can\'t be located</div><span class="tag">'+slot+'</span>';}
    }

    /* ══ ⚠⚠ THE VAULT ENLARGE'S STAGE — THE OWNER'S SIDE OF THE SAME
     * SHAPE `vPublicZoom` USES, AND IT IS NOT A COPY BY ACCIDENT ═══════
     *
     * ⚠ THE GUARD IS `shown[z]`, NOT `state.vaultZoom` ALONE. Identical
     * reasoning to the Community enlarge: the condition that filled the
     * frame is READ AGAIN rather than trusted from the last render, so a
     * viewer open over a side that has since failed asks for nothing.
     *
     * ⚠⚠ THE `display` COPY GOES UP SYNCHRONOUSLY. Those bytes are in
     * hand — they are what the frame behind the viewer is showing — so
     * the stage is never black. Jesse confirmed on a phone 2026-09-03
     * that the Community version of this "never looks broken"; ⚠ what he
     * could NOT confirm is the swap to `archive` actually happening,
     * which is an open measurement against BOTH viewers now, not just
     * that one. Do not read this comment as saying the swap is observed.
     *
     * ⚠ RE-READ THE ELEMENT AFTER THE AWAIT — a render during the fetch
     * replaces this screen's innerHTML, and writing to the detached node
     * would put the archive bytes on a stage nobody is looking at. */
    const z=state.vaultZoom;
    if(z&&shown[z]){
      const zi=document.getElementById("vpz_img");
      if(zi)zi.src=shown[z];
      const r=await view(h,z,TIER_FOR.zoom,p[z]||null);
      const zi2=document.getElementById("vpz_img");
      if(r.url&&zi2)zi2.src=r.url;
      renderVaultZoomNotice(r.url?null:r.state);
    }
  }}
  /* ══ ⚠⚠⚠ THE COMMUNITY READ PATH — STEP 7 BULLET 2 ═══════════════════
   *
   * THIS LINE USED TO CALL `getPubPhoto`, WHICH READ
   * `notevault:pubph:<user>:<hid>:<slot>` — a key `remote.js` routes to
   * LOCAL IndexedDB. So a stranger read their OWN browser under someone
   * else's username and found nothing, and **no stranger has ever seen a
   * Community photograph.** Not a regression and not something Act 4
   * caused: it has never worked, from the day the feature shipped.
   *
   * ⚠ ONE REQUEST FOR THE WHOLE PROFILE, NOT ONE PER PHOTOGRAPH. The
   * single route costs 2 PostgREST reads per photograph for a stranger,
   * so a 20-note profile was 40 reads and a 100-note profile 200. The
   * batch route is 2 reads regardless of note count — 1 when the profile
   * is the caller's own. Jesse: *"Two reads per profile view rather than
   * two per photograph is the difference between a feature that works and
   * one I'd have to ration."*
   *
   * ⚠⚠ FRONT SLOT, THUMB TIER, AND THAT IS WHAT THE SCREEN RENDERS.
   * `views-community.js` gives a tile an id only when `x.hasF && x.hid`,
   * and the tile is a 46 px chip.
   *
   * ⚠⚠⚠ AND THE UNAVAILABLE-VS-MISSING SPLIT IS PER REQUEST HERE, NOT
   * PER PHOTOGRAPH — see `community-photos.js`'s header for why the
   * endpoint cannot carry it per photograph and why inventing one would
   * be a guess. An id that did not come back leaves the tile a plain
   * denomination chip; a request that FAILED says so once, above the
   * list, with a Retry. */
  if(state.screen==="pubprofile"){
    const p=(state.pubProfiles||[]).find(x=>x.username===state.viewUser);
    if(p){
      const ids=p.notes.filter(x=>x.hasF&&x.hid).map(x=>x.hid);
      const r=await loadCommunityThumbs({
        username:p.username,ids,slot:"front",tier:TIER_FOR.community,tokenImpl:getToken,
      });
      for(const x of p.notes){
        if(!x.hasF||!x.hid)continue;
        const el=document.getElementById("pth_"+x.hid);if(!el)continue;
        const url=r.photos.get(x.hid);
        if(url){el.classList.add("hasimg");el.innerHTML='<img alt="note">';el.firstChild.src=url;}
      }
      renderPhotoNotice(r);
    }
  }

  /* ══ ⚠⚠⚠ THE PUBLIC NOTE DETAIL — PHASE 4 STEP 9, PART ONE ══════════
   *
   * Two frames, front and back, at `display`. The screen itself landed
   * in the previous session routing and rendering but NOT hydrating;
   * this is the half that puts pixels in it.
   *
   * ⚠ IT GOES THROUGH THE BATCH ROUTE, NOT THE PER-PHOTOGRAPH ONE, AND
   * THE REASON IS NOT COST. For a single note the two are the same
   * price — the single route is 2 PostgREST reads per photograph and
   * this batch of one is 2 per request, so both come to 4 for a
   * two-sided note. `loadCommunityThumbs` is chosen because it is the
   * path that has been GATED AND SABOTAGE-PROVEN: seeded-empty result
   * map, ids dropped unless asked for, failures never rendered as
   * "unresolved". Hand-rolling a fetch here would re-open every one of
   * those by construction, to buy nothing.
   *
   * ⚠⚠ ONE REQUEST PER SLOT, BECAUSE THE ROUTE IS KEYED BY SLOT —
   * `/api/photos/batch/<subject>/<slot>/<tier>`. Front and back cannot
   * share a request, and that is the endpoint's shape rather than a
   * choice made here.
   *
   * ⚠⚠⚠ A SLOT THE NOTE DOES NOT CLAIM IS NEVER ASKED ABOUT. `hasF` /
   * `hasB` come from `rowToPublicNote`, and skipping the request leaves
   * the frame on its neutral "No back photograph" placeholder. This is
   * not an optimisation: asking anyway would spend a round trip to be
   * told what the note already said, and — the part that matters — its
   * `unresolved` answer is INDISTINGUISHABLE from "not yours to see".
   * Rendering that as a fault would accuse the collector of a broken
   * photograph where they simply never took one.
   *
   * ⚠ THE FRAMES BECAME BUTTONS ON 2026-09-02, AND THE OLD SENTENCE
   * HERE — "NO ENLARGE, NO TAP, BY DECISION" — WAS TRUE ONLY WHILE THE
   * "+1" WAS UNTAKEN. Jesse decided FOR the archive tier on 2026-08-29;
   * see `vPublicZoom` in views-community.js for the costing it carries.
   * What this loop owes that decision is below: `data-ppzoom` goes on a
   * frame ONLY in the branch that actually put a photograph in it, so
   * the affordance cannot outlive the thing it opens. */
  if(state.screen==="pubnote"){
    const p=(state.pubProfiles||[]).find(x=>x.username===state.viewUser);
    const x=p&&(p.notes||[]).find(y=>y.hid===state.pubNote);
    if(p&&x&&x.hid){
      let failed=null,served=0;
      /* ⚠ THE `display` BYTES ARE KEPT SLOT BY SLOT BECAUSE THE ENLARGE
       * NEEDS THEM SYNCHRONOUSLY. They are what goes on the stage while
       * the archive bytes are still on the wire — see the zoom block
       * below. */
      const shown={front:null,back:null};
      for(const slot of ["front","back"]){
        const f=document.getElementById("ppf_"+slot);if(!f)continue;
        if(!(slot==="front"?x.hasF:x.hasB))continue;
        const r=await loadCommunityThumbs({
          username:p.username,ids:[x.hid],slot,tier:TIER_FOR.detail,tokenImpl:getToken,
        });
        const url=r.photos.get(x.hid);
        if(url){
          f.classList.add("filled");
          f.innerHTML='<img alt="'+slot+'"><span class="tag">'+(slot==="front"?"Front":"Back")+'</span>';
          f.querySelector("img").src=url;
          /* ⚠⚠ THE TAP TARGET IS SET HERE AND NOWHERE ELSE — in the same
           * branch, under the same condition, as the photograph itself.
           * An empty side never reaches this line (the `hasF`/`hasB`
           * guard above skips it) and a failed fetch never reaches it
           * either (`url` is undefined). That is what keeps `.filled`,
           * `data-ppzoom` and the bytes ONE condition rather than three
           * that happen to agree today. */
          f.dataset.ppzoom=slot;
          shown[slot]=url;
          served++;
        }
        /* ⚠ THE FIRST FAILURE WINS AND THE SECOND SLOT IS STILL TRIED.
         * Two slots are two requests and so two failures; the screen
         * says the sentence ONCE, for the same reason the profile does
         * — one dropped connection is one fact, not two. */
        if(r.failed&&!failed)failed=r.failed;
      }
      /* ⚠⚠ A COPY, NEVER THE CACHED OBJECT. `loadCommunityThumbs` puts
       * that exact failure object into `cache.failures`; setting
       * `partial` on it in place would edit the cache from the
       * renderer. And `partial` has to be recomputed anyway: the
       * per-call flag means "this request got something", while the
       * sentence on screen is about BOTH frames. */
      renderPhotoNotice({failed:failed&&{...failed,partial:served>0}},"ppnotice","data-ppnretry");

      /* ══ ⚠⚠⚠ THE ENLARGE — THE "+1", AND THE ONLY PLACE THIS APP ASKS
       * A STRANGER'S SCREEN FOR `archive` ═══════════════════════════
       *
       * ⚠ THE GUARD IS `shown[z]`, NOT `state.pubZoom` ALONE, AND THAT
       * IS THE WHOLE SAFETY PROPERTY. The archive bytes are spent only
       * for a slot this pass actually filled — so a viewer left open
       * over a side that has since gone empty or failed asks for
       * nothing. It
       * is the same condition as the tap target, read a second time
       * rather than trusted from the last render.
       *
       * ⚠⚠ THE STAGE GETS THE `display` COPY FIRST, SYNCHRONOUSLY.
       * Those bytes are already in hand — they are what the frame
       * behind this overlay is showing — so the enlarge opens with a
       * photograph in it and sharpens when `archive` lands. Costs
       * nothing on the wire. The alternative is a black stage for
       * several seconds on a phone: a tap that looks broken until
       * suddenly it isn't. */
      const z=state.pubZoom;
      if(z&&shown[z]){
        const zi=document.getElementById("ppz_img");
        if(zi)zi.src=shown[z];
        const r=await loadCommunityThumbs({
          username:p.username,ids:[x.hid],slot:z,tier:TIER_FOR.zoom,tokenImpl:getToken,
        });
        const zurl=r.photos.get(x.hid);
        /* ⚠ RE-READ THE ELEMENT AFTER THE AWAIT. A render during the
         * fetch replaces this screen's innerHTML, and writing to the
         * detached node would put the archive bytes on a stage nobody
         * is looking at. */
        const zi2=document.getElementById("ppz_img");
        if(zurl&&zi2)zi2.src=zurl;
        renderZoomNotice(zurl?null:r.failed);
      }
    }
  }
}

/* ── ⚠⚠ THE ENLARGE'S OWN FAILURE IS A DIFFERENT SENTENCE FROM THE
 * FRAMES', AND SAYING THE FRAMES' SENTENCE HERE WOULD BE A LIE ──────
 * `renderPhotoNotice` says "Photographs couldn't load". Under an open
 * viewer that is FALSE: the `display` copy is on the stage, the
 * collector is looking at it. What failed is the full-detail tier and
 * nothing else, so this says exactly that and no more.
 *
 * ⚠ IT IS A SEPARATE FUNCTION RATHER THAN A THIRD PARAMETER ON
 * `renderPhotoNotice` BECAUSE THE SENTENCE DIFFERS, not just the
 * element and the attribute. Parameterising the two words that change
 * would have made one function that says two unrelated things.
 *
 * ⚠ NO NEW CSS — `.hint`, `.btn.sm`, `.ghost`, the same composition
 * `renderPhotoNotice` uses. */
function renderZoomNotice(failed){
  const el=document.getElementById("ppznotice");if(!el)return;
  if(!failed){el.innerHTML="";return;}
  if(!failed.retryable){
    el.innerHTML='<div class="hint" style="margin:0">Sign in to see this photograph in full detail.</div>';
    return;
  }
  el.innerHTML='<div class="hint" style="margin:0;display:flex;align-items:center;justify-content:center;gap:10px">'+
    '<span>⟳ Full detail couldn\'t load</span>'+
    '<button class="btn sm ghost" data-ppzretry>Retry</button></div>';
}

/* ── ⚠⚠ THE VAULT ENLARGE'S FAILURE LINE. SAME PLACE ON THE SCREEN AS
 * `renderZoomNotice`, DIFFERENT SENTENCES, AND IT TAKES A STATE RATHER
 * THAN A FAILURE OBJECT ─────────────────────────────────────────────
 * The Community enlarge fails against a ROUTE and gets a `failed` object
 * carrying `retryable`. The owner's own photograph resolves through
 * `view()`, which answers with a `STATE`, so this reads that instead.
 * Translating one into the other to share a function would have invented
 * a failure object that no code produces.
 *
 * ⚠ THE SPLIT IS THE SAME SPLIT THE FRAMES ALREADY MAKE, and it is the
 * reason this is not one sentence: UNAVAILABLE is transient and a Retry
 * can win, MISSING/BROKEN means the bytes are not on this device and a
 * button that cannot work is worse than none — `renderPhotoNotice`'s rule
 * for SIGNED_OUT, applied to the state that is its equivalent here.
 *
 * ⚠ AND NEITHER SENTENCE MAY SAY THE PHOTOGRAPH FAILED. The `display`
 * copy is on the stage and the collector is looking at it; what failed is
 * the full-detail tier and nothing else. Same trap `renderZoomNotice`
 * records.
 *
 * ⚠ NO NEW CSS — `.hint`, `.btn.sm`, `.ghost`, as everywhere else. */
function renderVaultZoomNotice(st){
  const el=document.getElementById("vpznotice");if(!el)return;
  if(!st){el.innerHTML="";return;}
  /* ⚠⚠ SPLIT 2026-09-03 FOR THE REASON THE TILE SPLIT: the shared
   * sentence asserted a LOCATION, and for BROKEN nobody knows one. See
   * the tile's branch for what BROKEN actually means.
   *
   * ⚠⚠⚠ BOTH OF THESE BRANCHES ARE UNREACHABLE FROM THE SCREEN TODAY,
   * AND SAYING SO IS THE POINT. The viewer opens only from `data-vzoom`,
   * which `hydratePhotos` sets only on a frame that filled; the stage
   * then re-resolves the SAME ref, so a slot that filled at `display`
   * cannot come back MISSING or BROKEN at `archive` — those states are
   * computed from the ref, not from the fetch. What can reach here is
   * UNAVAILABLE. ⚠ These two are written for correctness if a future
   * path reaches them and **must not be described as proven behaviour**;
   * no fixture can drive them through the screen. */
  if(st===STATE.MISSING){
    el.innerHTML='<div class="hint" style="margin:0">The full-detail copy isn\'t on this device.</div>';
    return;
  }
  if(st===STATE.BROKEN){
    el.innerHTML='<div class="hint" style="margin:0">This photograph can\'t be located.</div>';
    return;
  }
  el.innerHTML='<div class="hint" style="margin:0;display:flex;align-items:center;justify-content:center;gap:10px">'+
    '<span>⟳ Full detail couldn\'t load</span>'+
    '<button class="btn sm ghost" data-vpzretry>Retry</button></div>';
}

/* ── ⚠⚠ THE ONE PLACE `unavailable` IS SAID ON THIS SCREEN ───────────
 * It is a screen-level line rather than a per-tile marker because the
 * failure is a property of the REQUEST — one dropped connection is one
 * fact, not twenty. The tiles stay plain, so nothing claims to know why
 * an individual photograph is absent.
 *
 * ⚠ `SIGNED_OUT` GETS NO RETRY BUTTON. A retry could not succeed; the
 * fix is signing in, and a button that cannot work is worse than none.
 *
 * ⚠ NO NEW CSS. Composed from `.hint`, `.btn.sm` and `.ghost`, the way
 * `views-auth.js` builds its screens from existing classes plus inline
 * styles. Nothing on this screen needs a rule that does not exist.
 *
 * ⚠⚠ THE TARGET AND THE RETRY ATTRIBUTE ARE PARAMETERS AS OF STEP 9,
 * AND THE DEFAULTS KEEP THE PROFILE CALL BYTE-IDENTICAL. The note
 * detail needs the same sentence in a different element with a
 * different retry, and the alternative was a second copy of this
 * function — the two-copies-that-agree-today shape this project has
 * been burned by. The VALUE stays the username in both, because a
 * failure scope is `(username, subject, slot, tier)` and carries no
 * holding id: a retry on one note lifts the whole screen's block, which
 * is what it has to do to be a retry at all.
 *
 * ⚠ THE USERNAME IN THE RETRY ATTRIBUTE IS ESCAPED LIKE THE FOURTEEN
 * TYPING BOXES (the cleanup round, 2026-10-09; the fifteenth site, found
 * 2026-10-08 by the bundle's `&quot;` count). `"` alone was escaped
 * before; the handler reads the attribute back as the username to retry,
 * and a row written past the app's `[a-z0-9_]` cleaning can carry `&`
 * or `<` — nothing in db/ constrains the column. */
const escH=s=>String(s==null?"":s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
function renderPhotoNotice(r,elId="pphnotice",retryAttr="data-pphretry"){
  const el=document.getElementById(elId);if(!el)return;
  if(!r.failed){el.innerHTML="";return;}
  const some=r.failed.partial?"Some photographs":"Photographs";
  if(!r.failed.retryable){
    el.innerHTML='<div class="hint" style="margin:0 0 12px">Sign in to see this collector\'s photographs.</div>';
    return;
  }
  el.innerHTML='<div class="hint" style="margin:0 0 12px;display:flex;align-items:center;justify-content:center;gap:10px">'+
    '<span>⟳ '+some+' couldn\'t load</span>'+
    '<button class="btn sm ghost" '+retryAttr+'="'+escH(state.viewUser||"")+'">Retry</button></div>';
}

export {
  PHOTO_KEY,
  claimsMissingPhoto,
  RAW_KEY,
  SPEC_KEY,
  getPhoto,
  holdChip,
  hydratePhotos,
  putPhoto,
}
